import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc, Chart } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import IndexScreenCard from '../../../../components/navigation/indexScreenCards/IndexScreenCard';
import { classNames } from '../../../../style/classNames';

export const Route = createFileRoute('/_authenticated/$patientId/statement/')({
  component: StatementIndex,
});

function StatementIndex() {
  const { currentUser } = useAuth();
  const patientId = currentUser?.uid || '';
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutate(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutate(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (!patientInfo || !patientInfo.navObj) {
    return <p>Loading...</p>;
  }

  return (
    <>
      <IndexScreenCard
        patientId={patientId}
        currentPath={pathname}
        youWill={[
          'Legally attest to the statement you are about to record.',
          'View videos to learn about the recording process.',
          'Record your MIDEO safety statement.',
          'Sign your MIDEO safety statement.',
          'Invite two witnesses to view and sign your statement.',
        ]}
        specialInstructions={
          'Your healthcare agent or any relatives are not able to serve as your witness. Please have the email addresses of two other trusted individuals ready to complete this section.'
        }
      />
      <button onClick={handleNextButtonClick} className={classNames.nextButton}>
        Next
      </button>
    </>
  );
}

export default StatementIndex;
